import { createContext, useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { getBillingAction, getCurrentPlanInterval, isEntitledSubscription } from '@/lib/billingState'
import type { BillingAction } from '@/lib/billingState'
import type { PriceInterval, Subscription } from '../types/database'

interface SubscriptionContextType {
  subscription: Subscription | null
  loading: boolean
  hasActiveSubscription: boolean
  hasEntitledAccess: boolean
  billingAction: BillingAction
  currentPlanInterval: PriceInterval | null
  isTrialing: boolean
  isTrialActive: boolean
  trialDaysRemaining: number
  trialEndsAt: string | null
  refreshSubscription: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

export { SubscriptionContext }

function getTrialInfo(subscription: Subscription | null) {
  if (subscription?.status !== 'trialing' || !subscription.current_period_end) {
    return { isActive: false, daysRemaining: 0, trialEndsAt: null as string | null }
  }

  const trialEndsAt = new Date(subscription.current_period_end)
  if (Number.isNaN(trialEndsAt.getTime())) {
    return { isActive: false, daysRemaining: 0, trialEndsAt: null as string | null }
  }

  const msRemaining = trialEndsAt.getTime() - Date.now()
  const isActive = msRemaining > 0

  return {
    isActive,
    daysRemaining: isActive ? Math.max(1, Math.ceil(msRemaining / (1000 * 60 * 60 * 24))) : 0,
    trialEndsAt: trialEndsAt.toISOString(),
  }
}

export function SubscriptionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [trialInfo, setTrialInfo] = useState(() => getTrialInfo(null))
  const [loading, setLoading] = useState(true)
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null)

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setSubscription(null)
      setTrialInfo(getTrialInfo(null))
      setLoading(false)
      setResolvedUserId(null)
      return
    }

    try {
      const subscriptionQuery = supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      const { data: subscriptionData, error: subscriptionError } = await subscriptionQuery

      if (subscriptionError && subscriptionError.code !== 'PGRST116') {
        // PGRST116 = no rows returned, which means no subscription
        console.error('Error fetching subscription:', subscriptionError)
      }

      setSubscription(subscriptionData || null)
      setTrialInfo(getTrialInfo(subscriptionData || null))
    } catch (err) {
      console.error('Error fetching subscription:', err)
      setSubscription(null)
      setTrialInfo(getTrialInfo(null))
    } finally {
      setLoading(false)
      setResolvedUserId(user.id)
    }
  }, [user])

  // Fetch subscription when user changes
  useEffect(() => {
    setLoading(true)
    setResolvedUserId(null)
    fetchSubscription()
  }, [fetchSubscription])

  // Subscribe to realtime changes
  useEffect(() => {
    if (!user) return

    const channel = supabase
      .channel('subscription_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Refetch on any change to the subscription
          fetchSubscription()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user, fetchSubscription])

  const isResolvedForCurrentUser = !user || resolvedUserId === user.id
  const hasActiveSubscription = subscription?.status === 'active'
  const hasEntitledAccess = isEntitledSubscription(subscription)

  const value = {
    subscription,
    loading: loading || !isResolvedForCurrentUser,
    hasActiveSubscription,
    hasEntitledAccess,
    billingAction: getBillingAction(subscription),
    currentPlanInterval: getCurrentPlanInterval(subscription),
    isTrialing: subscription?.status === 'trialing',
    isTrialActive: trialInfo.isActive,
    trialDaysRemaining: trialInfo.daysRemaining,
    trialEndsAt: trialInfo.trialEndsAt,
    refreshSubscription: fetchSubscription,
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}
